export type GroupMeOnlyRsvpStatus = 'Going' | 'Not Going' | 'Maybe' | 'Unknown';
export type GroupMeOnlyMatchStatus = 'matched' | 'ambiguous' | 'unmatched';

export type GroupMeOnlyParticipantSummaryInput = {
  latest_rsvp_status: GroupMeOnlyRsvpStatus | string | null;
  match_status: GroupMeOnlyMatchStatus | string | null;
  players?: { sex?: string | null; gender?: string | null } | null;
};

export type GroupMeOnlyRsvpSummary = {
  goingTotal: number;
  goingMen: number;
  goingWomen: number;
  goingUnspecifiedMatched: number;
  goingUnmatched: number;
  notGoingTotal: number;
  notGoingMen: number;
  notGoingWomen: number;
  notGoingUnspecifiedMatched: number;
  notGoingUnmatched: number;
  maybeTotal: number;
  unknownTotal: number;
  unmatchedTotal: number;
  ambiguousTotal: number;
};

export type GroupMeOnlyGoingParticipantGroups<T> = {
  men: T[];
  women: T[];
  unmatched: T[];
};

export type GroupMeTeamEventType = 'Practice' | 'Social' | 'Other';

export function isGroupMeMatchEventTitle(title: string | null | undefined): boolean {
  return /^\s*vs\b/i.test(title || '');
}

export function inferGroupMeTeamEventType(title: string | null | undefined): GroupMeTeamEventType {
  const normalized = title || '';
  if (/(practice|mixer)/i.test(normalized)) return 'Practice';
  if (/(party|social|happy\s+hour)/i.test(normalized)) return 'Social';
  return 'Other';
}

function normalizedGender(value: string | null | undefined): 'male' | 'female' | 'unspecified' {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'male' || normalized === 'm') return 'male';
  if (normalized === 'female' || normalized === 'f') return 'female';
  return 'unspecified';
}

export function summarizeGroupMeOnlyRsvps(participants: GroupMeOnlyParticipantSummaryInput[]): GroupMeOnlyRsvpSummary {
  const summary: GroupMeOnlyRsvpSummary = {
    goingTotal: 0,
    goingMen: 0,
    goingWomen: 0,
    goingUnspecifiedMatched: 0,
    goingUnmatched: 0,
    notGoingTotal: 0,
    notGoingMen: 0,
    notGoingWomen: 0,
    notGoingUnspecifiedMatched: 0,
    notGoingUnmatched: 0,
    maybeTotal: 0,
    unknownTotal: 0,
    unmatchedTotal: 0,
    ambiguousTotal: 0,
  };

  participants.forEach((participant) => {
    const status = participant.latest_rsvp_status || 'Unknown';
    const matchStatus = participant.match_status || 'unmatched';
    const gender = normalizedGender(participant.players?.sex || participant.players?.gender);

    if (matchStatus === 'unmatched') summary.unmatchedTotal += 1;
    if (matchStatus === 'ambiguous') summary.ambiguousTotal += 1;

    if (status === 'Going') {
      summary.goingTotal += 1;
      if (matchStatus !== 'matched') summary.goingUnmatched += 1;
      else if (gender === 'male') summary.goingMen += 1;
      else if (gender === 'female') summary.goingWomen += 1;
      else summary.goingUnspecifiedMatched += 1;
    } else if (status === 'Not Going') {
      summary.notGoingTotal += 1;
      if (matchStatus !== 'matched') summary.notGoingUnmatched += 1;
      else if (gender === 'male') summary.notGoingMen += 1;
      else if (gender === 'female') summary.notGoingWomen += 1;
      else summary.notGoingUnspecifiedMatched += 1;
    } else if (status === 'Maybe') {
      summary.maybeTotal += 1;
    } else {
      summary.unknownTotal += 1;
    }
  });

  return summary;
}

export function groupGroupMeOnlyGoingParticipants<T extends GroupMeOnlyParticipantSummaryInput>(
  participants: T[],
): GroupMeOnlyGoingParticipantGroups<T> {
  const groups: GroupMeOnlyGoingParticipantGroups<T> = { men: [], women: [], unmatched: [] };
  participants.forEach((participant) => {
    if (participant.latest_rsvp_status !== 'Going') return;
    if (participant.match_status !== 'matched') {
      groups.unmatched.push(participant);
      return;
    }
    const gender = normalizedGender(participant.players?.sex || participant.players?.gender);
    if (gender === 'male') groups.men.push(participant);
    else if (gender === 'female') groups.women.push(participant);
    else groups.unmatched.push(participant);
  });
  return groups;
}

export function groupMeOnlyEventDisplayDate(eventStartAt: string | null | undefined, sourceUpdatedAt: string | null | undefined): string {
  const value = eventStartAt || sourceUpdatedAt;
  if (!value) return 'Date unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function normalizeTwoDigitYear(year: number): number {
  if (year >= 100) return year;
  return year >= 70 ? 1900 + year : 2000 + year;
}

function safeDate(year: number, month: number, day: number): string | null {
  const normalizedYear = normalizeTwoDigitYear(year);
  const date = new Date(Date.UTC(normalizedYear, month - 1, day));
  if (
    date.getUTCFullYear() !== normalizedYear
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }
  return date.toISOString().slice(0, 10);
}

export function parseGroupMeOnlyEventDateFromTitle(title: string | null | undefined): string | null {
  const normalized = (title || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return null;

  const numericMatch = normalized.match(/(?:^|[^0-9])([0-1]?[0-9])[/-]([0-3]?[0-9])[/-]([0-9]{2}|[0-9]{4})(?:[^0-9]|$)/);
  if (numericMatch) {
    return safeDate(Number(numericMatch[3]), Number(numericMatch[1]), Number(numericMatch[2]));
  }

  const monthMatch = normalized.match(/(?:^|[^A-Za-z])(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+([0-3]?[0-9])(?:st|nd|rd|th)?(?:,)?\s+([0-9]{2}|[0-9]{4})(?:[^0-9]|$)/i);
  if (!monthMatch) return null;

  const month = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
    .indexOf(monthMatch[1].slice(0, 3).toLowerCase()) + 1;
  if (month <= 0) return null;
  return safeDate(Number(monthMatch[3]), month, Number(monthMatch[2]));
}
